<template>

  <PageHeader
    title="Alert Instance Management"
    subtitle="Monitor and manage system-generated metric alerts"
    :breadcrumbs="[{ label: 'Admin Console' }]"
  >
    <template #actions>
      <div class="ml-auto flex gap-2">
        <button
          class="btn-primary"
          @click="refreshData"
        >
          <font-awesome-icon :icon="['fas', 'refresh']" :spin="loading" />
          Refresh
        </button> 
      </div>
    </template>
    <template #bottom>
      <Page_Tabs
        class="px-6 pt-4"
        :tabs="tabs"
        :initial-tab="activeTab"
        no-border
        @select="activeTab = $event"
      />
    </template>
  </PageHeader>

  <div class="p-4">

    <!-- Stats Cards -->
    <div class="grid grid-cols-5 gap-4 mb-4">
      <div
        v-for="stat in statsCards"
        :key="stat.key"
        class="bg-white rounded-lg border border-gray-200 p-4"
      >
        <div class="flex items-center justify-between">
          <div>
            <p class="text-xs font-medium text-gray-500 uppercase">{{ stat.label }}</p>
            <p class="text-2xl font-bold" :class="stat.valueColor">{{ stat.value }}</p>
          </div>
          <font-awesome-icon :icon="stat.icon" class="text-2xl" :class="stat.iconColor" />
        </div>
      </div>
    </div>

    <!-- Tab Content -->

    <!-- Alert Types Tab -->
    <div v-if="activeTab === 'Alert Types'" class="bg-white rounded-lg border border-gray-200">
      <ComponentHeader title="Alert Types" subtitle="Manage alert definitions and their instances" />
      <div class="p-4">

          <!-- Alert Types Grid -->
          <div class="space-y-4">
            <div
              v-for="alertType in alertTypes"
              :key="alertType.id"
              class="bg-gray-50 rounded-lg border border-gray-200 p-4"
            >
              <div class="flex items-start justify-between">
                <div class="flex-1">
                  <div class="flex items-center gap-3 mb-2">
                    <h3 class="text-lg font-semibold text-gray-900">{{ alertType.name }}</h3>
                    <span
                      :class="[
                        'px-2 py-1 text-xs font-medium rounded-full',
                        alertType.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-600'
                      ]"
                    >
                      {{ alertType.is_active ? 'Active' : 'Inactive' }}
                    </span>
                    <span class="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                      {{ alertType.check_frequency }}
                    </span>
                  </div>

                  <p class="text-sm text-gray-600 mb-3">{{ alertType.description }}</p>

                  <div class="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span class="font-medium text-gray-700">Metric:</span>
                      <span class="ml-2 text-gray-600">{{ alertType.metric_name }}</span>
                    </div>
                    <div>
                      <span class="font-medium text-gray-700">Detection Type:</span>
                      <span class="ml-2 text-gray-600">{{ formatDetectionType(alertType.detection_config.type) }}</span>
                    </div>
                    <div>
                      <span class="font-medium text-gray-700">Total Instances:</span>
                      <span class="ml-2 font-semibold text-gray-900">{{ alertType.instance_count || 0 }}</span>
                    </div>
                  </div>

                  <!-- Instance Status Breakdown -->
                  <div v-if="alertType.instance_count > 0" class="mt-3 flex gap-4 text-xs">
                    <span class="text-green-600">
                      <FontAwesomeIcon :icon="['fas', 'circle']" class="mr-1" />
                      Active: {{ alertType.active_count || 0 }}
                    </span>
                    <span class="text-blue-600">
                      <FontAwesomeIcon :icon="['fas', 'eye']" class="mr-1" />
                      Acknowledged: {{ alertType.acknowledged_count || 0 }}
                    </span>
                    <span class="text-gray-600">
                      <FontAwesomeIcon :icon="['fas', 'check']" class="mr-1" />
                      Resolved: {{ alertType.resolved_count || 0 }}
                    </span>
                    <span class="text-orange-600">
                      <FontAwesomeIcon :icon="['fas', 'clock']" class="mr-1" />
                      Snoozed: {{ alertType.snoozed_count || 0 }}
                    </span>
                  </div>
                </div>

                <!-- Actions -->
                <div class="flex gap-2 ml-4">
                  <button
                    class="btn-primary-sm"
                    :disabled="refreshingAlerts[alertType.id]"
                    @click="refreshAlertType(alertType)"
                  >
                    <font-awesome-icon
                      :icon="['fas', refreshingAlerts[alertType.id] ? 'spinner' : 'sync']"
                      :spin="refreshingAlerts[alertType.id]"
                    />
                    Refresh
                  </button>

                  <button
                    class="btn-secondary"
                    :disabled="alertType.instance_count === 0"
                    @click="confirmArchiveAll(alertType)"
                  >
                    <font-awesome-icon :icon="['fas', 'box-archive']" />
                    Archive All
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div v-if="alertTypes.length === 0" class="text-center py-12 text-gray-500">
            <font-awesome-icon :icon="['fas', 'bell-slash']" class="text-4xl mb-3 text-gray-300" />
            <p>No alert types configured</p>
          </div>
      </div>
    </div>

    <!-- Alert Instances Tab -->
    <div v-if="activeTab === 'Alert Instances'" class="bg-white rounded-lg border border-gray-200">
      <ComponentHeader title="Alert Instances" subtitle="View and manage individual alert instances" />
      <div class="p-4">
        <MetricAlertsTable
          :auto-height="false"
          :pre-loaded-data="allInstances"
        />
      </div>
    </div>

    <!-- Bulk Operations Tab -->
    <div v-if="activeTab === 'Bulk Operations'" class="bg-white rounded-lg border border-gray-200">
      <ComponentHeader title="Bulk Operations" subtitle="Perform batch actions on alert instances" />
      <div class="p-4 space-y-6">
            <!-- Refresh All Alerts -->
            <div class="bg-blue-50 rounded-lg border border-blue-200 p-6">
              <h3 class="text-lg font-semibold text-blue-900 mb-2">
                <font-awesome-icon :icon="['fas', 'sync']" class="mr-2" />
                Refresh All Alert Types
              </h3>
              <p class="text-sm text-blue-700 mb-4">
                Run detection for all active alert types and create new alert instances for any findings.
                This will not duplicate existing alerts.
              </p>
              <button
                class="btn-primary"
                :disabled="refreshingAll"
                @click="refreshAllAlerts"
              >
                <font-awesome-icon
                  :icon="['fas', refreshingAll ? 'spinner' : 'sync']"
                  :spin="refreshingAll"
                />
                {{ refreshingAll ? 'Refreshing...' : 'Refresh All Alerts' }}
              </button>
            </div>

            <!-- Delete Old Alerts -->
            <div class="bg-red-50 rounded-lg border border-red-200 p-6">
              <h3 class="text-lg font-semibold text-red-900 mb-2">
                <FontAwesomeIcon :icon="['fas', 'trash']" class="mr-2" />
                Delete Old Alert Instances
              </h3>
              <p class="text-sm text-red-700 mb-4">
                Remove resolved or aged-out alert instances older than the specified date.
                This action cannot be undone.
              </p>
              <div class="flex gap-4 items-end">
                <div>
                  <label class="block text-xs font-medium text-red-700 mb-1">Delete alerts older than</label>
                  <input
                    v-model="bulkDeleteDate"
                    type="date"
                    class="px-3 py-2 text-sm border border-red-300 rounded-lg"
                  />
                </div>
                <button
                  class="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white
                         rounded-lg font-medium transition-colors"
                  :disabled="!bulkDeleteDate"
                  @click="confirmBulkDelete"
                >
                  <font-awesome-icon :icon="['fas', 'trash']" class="mr-2" />
                  Delete Old Alerts
                </button>
              </div>
            </div>
      </div>
    </div>

    <!-- Archive Confirmation Modal -->
    <transition name="modal">
      <div v-if="showArchiveModal" class="fixed inset-0 z-50 overflow-y-auto">
        <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div class="fixed inset-0 transition-opacity" @click="cancelArchive">
            <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
          </div>

          <!-- `relative z-10` is load-bearing: under Tailwind v4 a bare `transform`
               computes to none and creates no stacking context, so the fixed
               backdrop above paints over this panel and swallows every click. -->
          <div
            class="relative z-10 inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl
                   transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
          >
            <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div class="sm:flex sm:items-start">
                <div
                  class="mx-auto shrink-0 flex items-center justify-center h-12 w-12 rounded-full
                         bg-amber-100 sm:mx-0 sm:h-10 sm:w-10"
                >
                  <font-awesome-icon :icon="['fas', 'box-archive']" class="h-6 w-6 text-amber-600" />
                </div>
                <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 class="text-lg leading-6 font-medium text-gray-900">
                    Archive All Alert Instances
                  </h3>
                  <div class="mt-2">
                    <p class="text-sm text-gray-500">
                      Archive all {{ archivingAlertType?.instance_count || 0 }} instances
                      of "{{ archivingAlertType?.name }}"? They will no longer appear anywhere in the
                      app. Detection can raise this alert again at the next observation date.
                    </p>
                    <div v-if="archivingAlertType?.active_count > 0" class="mt-3 bg-amber-50 border border-amber-200 rounded-md p-3">
                      <p class="text-sm text-amber-800">
                        <font-awesome-icon :icon="['fas', 'exclamation-circle']" class="mr-1" />
                        Note: This includes {{ archivingAlertType.active_count }} active alerts that have not been resolved.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2
                       bg-amber-600 text-base font-medium text-white hover:bg-amber-700 disabled:bg-gray-400
                       focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 sm:ml-3 sm:w-auto sm:text-sm"
                :disabled="archiving"
                @click="executeArchive"
              >
                <font-awesome-icon v-if="archiving" :icon="['fas', 'spinner']" :spin="true" class="mr-2" />
                {{ archiving ? 'Archiving...' : 'Archive All' }}
              </button>
              <button
                type="button"
                class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2
                       bg-white text-base font-medium text-gray-700 hover:bg-gray-50 disabled:bg-gray-200
                       focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-dark sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                :disabled="archiving"
                @click="cancelArchive"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import MetricAlertsTable from '@/components/Metric/Metric_Tables/Metric_Alerts_Table.vue'
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';
import Page_Tabs from '@/components/widgetcomponents/Page_Tabs.vue';

export default {
  name: 'AlertInstanceAdmin',

  components: {
    FontAwesomeIcon,
    MetricAlertsTable,
    PageHeader,
    ComponentHeader,
    Page_Tabs
  },

  data() {
    return {
      tabs: ['Alert Types', 'Alert Instances', 'Bulk Operations'],
      activeTab: 'Alert Types',

      // Data
      alertTypes: [],
      allInstances: [],

      // Loading states
      loading: false,
      refreshingAlerts: {},
      refreshingAll: false,
      archiving: false,

      // Archive modal
      showArchiveModal: false,
      archivingAlertType: null,

      // Bulk operations
      bulkDeleteDate: ''
    }
  },

  computed: {
    alertStats() {
      return this.alertTypes.reduce((stats, type) => {
        stats.total += type.instance_count || 0
        stats.active += type.active_count || 0
        stats.acknowledged += type.acknowledged_count || 0
        stats.resolved += type.resolved_count || 0
        stats.snoozed += type.snoozed_count || 0
        if (type.instance_count > 0) stats.uniqueTypes++
        return stats
      }, { total: 0, active: 0, acknowledged: 0, resolved: 0, snoozed: 0, uniqueTypes: 0 })
    },

    statsCards() {
      const stats = this.alertStats
      return [
        {
          key: 'total',
          label: 'Total Alerts',
          value: stats.total,
          valueColor: 'text-gray-900',
          icon: ['fas', 'bell'],
          iconColor: 'text-gray-300'
        },
        {
          key: 'active',
          label: 'Active',
          value: stats.active,
          valueColor: 'text-green-600',
          icon: ['fas', 'circle-check'],
          iconColor: 'text-green-300'
        },
        {
          key: 'acknowledged',
          label: 'Acknowledged',
          value: stats.acknowledged,
          valueColor: 'text-blue-600',
          icon: ['fas', 'eye'],
          iconColor: 'text-blue-300'
        },
        {
          key: 'resolved',
          label: 'Resolved',
          value: stats.resolved,
          valueColor: 'text-gray-600',
          icon: ['fas', 'check-double'],
          iconColor: 'text-gray-300'
        },
        {
          key: 'types',
          label: 'Alert Types',
          value: stats.uniqueTypes,
          valueColor: 'text-purple-600',
          icon: ['fas', 'layer-group'],
          iconColor: 'text-purple-300'
        }
      ]
    }
  },

  async mounted() {
    await this.loadAlertTypes()
  },

  methods: {
    async loadAlertTypes() {
      this.loading = true
      try {
        const response = await axios.get('/api/impact_analytics/composable-metric-alerts/')
        const alertTypes = response.data?.results || response.data

        const instanceResponse = await axios.get('/api/impact_analytics/composable-alert-instances/')
        const allInstances = instanceResponse.data.results || instanceResponse.data

        this.allInstances = allInstances

        // Group instances by alert_id and calculate counts
        const instancesByAlertType = {}
        allInstances.forEach(instance => {
          const alertId = instance.alert
          if (!instancesByAlertType[alertId]) {
            instancesByAlertType[alertId] = {
              all: [],
              active: 0,
              acknowledged: 0,
              resolved: 0,
              snoozed: 0
            }
          }
          
          instancesByAlertType[alertId].all.push(instance)
          
          // Count by status
          if (instance.status === 'active') instancesByAlertType[alertId].active++
          else if (instance.status === 'acknowledged') instancesByAlertType[alertId].acknowledged++
          else if (instance.status === 'resolved') instancesByAlertType[alertId].resolved++
          else if (instance.status === 'snoozed') instancesByAlertType[alertId].snoozed++
        })

        // Attach counts to alert types
        this.alertTypes = alertTypes.map(type => {
          const instances = instancesByAlertType[type.id] || { all: [], active: 0, acknowledged: 0, resolved: 0, snoozed: 0 }
          
          return {
            ...type,
            instance_count: instances.all.length,
            active_count: instances.active,
            acknowledged_count: instances.acknowledged,
            resolved_count: instances.resolved,
            snoozed_count: instances.snoozed
          }
        })

      } catch (error) {
        console.error('Failed to load alert types:', error)
      } finally {
        this.loading = false
      }
    },



    async refreshAlertType(alertType) {
      this.refreshingAlerts[alertType.id] = true
      try {
        const response = await axios.post(`/api/impact_analytics/composable-metric-alerts/${alertType.id}/detect/`)

        const createdCount = response.data.created || 0
        console.log(`Created ${createdCount} new alert instances for ${alertType.name}`)

        await this.loadAlertTypes()
      } catch (error) {
        console.error('Failed to refresh alert type:', error)
      } finally {
        this.refreshingAlerts[alertType.id] = false
      }
    },

    async refreshAllAlerts() {
      this.refreshingAll = true
      try {
        const response = await axios.post('/api/impact_analytics/composable-metric-alerts/detect_all/')
        const totalCreated = (response.data.alerts || []).reduce((sum, r) => sum + (r.created || 0), 0)
        console.log(`Refreshed all alerts. Created ${totalCreated} new instances.`)
        await this.loadAlertTypes()
      } catch (error) {
        console.error('Failed to refresh all alerts:', error)
      } finally {
        this.refreshingAll = false
      }
    },



    confirmArchiveAll(alertType) {
      this.archivingAlertType = alertType
      this.showArchiveModal = true
    },

    async executeArchive() {
      if (!this.archivingAlertType) return

      this.archiving = true
      try {
        // Server-side bulk archive — the only endpoint that can set archived_at.
        // Archives every instance of the alert type regardless of status.
        const response = await axios.post(
          `/api/impact_analytics/composable-metric-alerts/${this.archivingAlertType.id}/archive_instances/`
        )
        console.log(`Archived ${response.data.archived || 0} alert instances`)
        await this.loadAlertTypes()
        this.showArchiveModal = false
        this.archivingAlertType = null
      } catch (error) {
        console.error('Failed to archive alert instances:', error)
      } finally {
        this.archiving = false
      }
    },

    cancelArchive() {
      this.showArchiveModal = false
      this.archivingAlertType = null
    },

    async confirmBulkDelete() {
      if (!this.bulkDeleteDate) return

      if (!confirm(`Delete all resolved and aged-out alerts older than ${this.bulkDeleteDate}?`)) {
        return
      }

      try {
        // Resolve all old resolved/expired instances older than bulkDeleteDate
        const cutoff = new Date(this.bulkDeleteDate)
        const stale = this.allInstances.filter(
          i => ['resolved', 'expired'].includes(i.status) && new Date(i.observation_date) < cutoff
        )
        await Promise.all(stale.map(i =>
          axios.patch(`/api/impact_analytics/composable-alert-instances/${i.id}/`, { status: 'resolved' })
        ))
        const deletedCount = stale.length
        console.log(`Resolved ${deletedCount} old alert instances`)

        // Reload data
        await this.loadAlertTypes()

        this.bulkDeleteDate = ''
      } catch (error) {
        console.error('Failed to delete old alerts:', error)
      }
    },

    refreshData() {
      this.loadAlertTypes()
    },



    formatStatus(status) {
      const labels = {
        active: 'Active',
        acknowledged: 'Acknowledged',
        resolved: 'Resolved',
        snoozed: 'Snoozed',
        erroneous: 'Erroneous',
        expired: 'Aged Out'
      }
      return labels[status] || status
    },

    formatDetectionType(type) {
      const labels = {
        'absolute_threshold': 'Absolute Threshold',
        'percentile_threshold': 'Percentile Threshold',
        'increasing_trend': 'Increasing Trend',
        'decreasing_trend': 'Decreasing Trend',
        'volatility': 'High Volatility'
      }
      return labels[type] || type
    }
  }
}
</script>

<style scoped>
.modal-enter-active, .modal-leave-active {
  transition: opacity 0.3s;
}
.modal-enter, .modal-leave-to {
  opacity: 0;
}

:deep(.ag-header-cell-text) {
  font-weight: 600;
  color: #374151;
}

:deep(.ag-cell) {
  display: flex;
  align-items: center;
}
</style>
